import Button, { ButtonProps, ButtonPropsColorOverrides } from '@mui/material/Button'
import { OverridableStringUnion } from '@mui/types'
import { FC, MouseEventHandler, ReactNode } from 'react'

export type C1ButtonColorType = OverridableStringUnion<
  'primary' | 'error' | 'info' | 'success' | 'inherit',
  ButtonPropsColorOverrides
>

/** @designSystem */
type C1ButtonBaseProps = Omit<
  ButtonProps,
  'color' | 'size' | 'disableElevation' | 'disableFocusRipple' | 'variant'
> & {
  /** Button style variant. Contained buttons cannot use info color. */
  variant?: 'outlined' | 'text' | 'contained';
  /** Button color theme. Available colors depend on variant. */
  color?: C1ButtonColorType;
  /** Click handler. */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Icon placed before the button label. Use `<C1Icon icon={...} />` for Lucide icons. */
  startIcon?: ReactNode;
  /** Icon placed after the button label. Use `<C1Icon icon={...} />` for Lucide icons. */
  endIcon?: ReactNode;
  /** Disables the button and prevents interaction. */
  disabled?: boolean;
  /** HTML button type. Set to `"submit"` inside forms. */
  type?: 'button' | 'submit' | 'reset';
};

// Contained variant cannot use 'info' color
export type ContainedButtonProps = {
  variant: 'contained';
  color?: Exclude<C1ButtonColorType, 'info'>;
}

// Outlined/Text variant can use any color
type OutlinedOrTextButtonProps = {
  variant?: 'outlined' | 'text';
  color?: C1ButtonColorType;
};

export type C1ButtonProps =
  & C1ButtonBaseProps
  & (ContainedButtonProps | OutlinedOrTextButtonProps)

/**
 * Primary interactive element for triggering actions.
 *
 * Thin wrapper around MUI Button that removes `size`, `disableElevation`, and
 * `disableFocusRipple`, and restricts `variant` and `color` to the C1 design
 * system palette.
 *
 * ## Variants
 * - `outlined` — default. Use for most actions: cancel, secondary confirm, filter controls
 * - `contained` — high emphasis. Use for primary call-to-action: save, create, submit
 * - `text` — minimal emphasis. Use for tertiary actions, inline links, or clear/reset controls
 *
 * ## Colors
 * - `primary` — default. Standard actions (save, continue, add)
 * - `error` — destructive actions (delete, remove, revoke)
 * - `success` — positive confirmations (approve, enable)
 * - `info` — neutral low-emphasis actions (clear filters, reset). **Not available with `contained` variant.**
 * - `inherit` — inherits color from parent context
 *
 * ## Behavior
 * - The `contained` + `info` combination is disallowed at the type level via a discriminated union
 * - Elevation and focus ripple are always disabled to match C1 visual style
 * - Pass Lucide icons via `startIcon` / `endIcon` wrapped in `<C1Icon icon={...} />`
 *
 * @designSystem
 * @category atoms
 * @relatedComponent C1LoadingButton | Use when the action is async and needs a loading indicator
 * @relatedComponent C1IconButton | Use for icon-only actions without a text label
 * @externalReference MUI Button API | https://mui.com/material-ui/api/button/
 */
export const C1Button: FC<C1ButtonProps> = (props) => <Button {...props} />
